import React, { useEffect } from 'react';
import { UserAuthHandlerMachineStateType } from 'libs/client-auth-manager/src/types/UserAuthHandlerMachineTypes';
import { Redirect, useHistory, useLocation } from 'react-router-dom';

import { getAccessToken, useSendUserEventApi } from '@nw/shared/data-access';
import { getQueryParamValue } from '@nw/shared/utils';

import LoginPage from '../../components/LoginPage';
import { ApplicationSectionNameEnums } from '../../constants/ApplicationSectionConstants';
import { NiatPortalClientBasedAuthConfig } from '../../constants/ClientBasedAuthConfig';
import { NIAT_APPLICATION_PORTAL_HOME_ROUTE } from '../../constants/RouteConstants';

/** Builds redirect URL: {domain}/application{search} */
const getApplicationRedirectUrl = (search: string): string => {
  const origin = window.location.origin.replace(/\/$/, '');
  const path = NIAT_APPLICATION_PORTAL_HOME_ROUTE; // '/application'
  return `${origin}${path}${search}`;
};

const LoginPageRoute: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { sendUserEventApi } = useSendUserEventApi();

  const onSuccessfulLogin = async (
    authMachineState: UserAuthHandlerMachineStateType
  ) => {
    const { userProfileData } = authMachineState.context;

    const eventData = {
      user_id: userProfileData?.user_id,
      utm_campaign: getQueryParamValue('utm_campaign') ?? '',
      utm_medium: getQueryParamValue('utm_medium') ?? '',
      utm_term: getQueryParamValue('utm_term') ?? '',
      invite_code: getQueryParamValue('invite_code') ?? '',
      utm_content: getQueryParamValue('utm_content') ?? '',
      utm_source: getQueryParamValue('utm_source') ?? '',
      custom_id: getQueryParamValue('custom_id') ?? '',
      phone_number: userProfileData?.phone_number ?? '',
      program_batch: getQueryParamValue('program_batch'),
      application_name_enum: ApplicationSectionNameEnums.NIAT_APPLICATION,
      pi: getQueryParamValue('pi'),
      pm: getQueryParamValue('pm')
    };

    await sendUserEventApi({
      integration_type: 'SEGMENT',
      activity_data: JSON.stringify(eventData),
      activity_id: '8086d84c-0c4e-46e7-b15c-319209e03766',
      activity_reference_id: userProfileData?.user_id ?? ''
    });

    // Check if there's a previous location to redirect to (set by ProtectedRoute)
    const from = location.state?.from;
    if (from) {
      // Redirect to the original location, preserving its search params
      return history.push({
        pathname: from.pathname,
        search: from.search,
        state: from.state
      });
    }

    // Redirect to {domain}/application{search}
    window.location.replace(getApplicationRedirectUrl(location.search));
  };

  const onFailedLogin = () => {};

  const isLoggedIn = getAccessToken() !== undefined;

  // Post form_initialized event when component renders
  useEffect(() => {
    window.postMessage('form_initialized', '*');
  }, []);

  // Handle redirect for logged-in users (fallback case when no 'from' location)
  useEffect(() => {
    if (isLoggedIn && !location.state?.from) {
      window.location.replace(getApplicationRedirectUrl(location.search));
    }
  }, [isLoggedIn, location]);

  if (isLoggedIn) {
    // Check if there's a previous location to redirect to (set by ProtectedRoute)
    const from = location.state?.from;
    if (from) {
      // Redirect to the original location, preserving its search params
      return (
        <Redirect
          to={{
            pathname: from.pathname,
            search: from.search,
            state: from.state
          }}
        />
      );
    }

    return null;
  }

  return (
    <>
      <LoginPage
        onSuccessfulLogin={onSuccessfulLogin}
        onFailedLogin={onFailedLogin}
        localConfig={NiatPortalClientBasedAuthConfig}
        shouldCallUserProfileDetails={true}
      />
    </>
  );
};

export default LoginPageRoute;
